import { useCallback, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { LOADING } from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { startGetCanvaFolders } from "../../actions/user";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

const SelectCanvaFolder = ({
  control,
  errors = {},
  rules = {},
  name,
  label,
  integrationID,
  disabled = false,
  placeholder,
  variant = "filled",
  fullWidth,
  helperText,
  onChangeAction = () => {},
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const onFetchFolders = useCallback(async () => {
    if (!integrationID) {
      setOptions([]);
      return;
    }
    setLoading(true);
    const response = await dispatch(startGetCanvaFolders({ integrationID }));
    if (response.ok) {
      const folders = response.data?.folders || [];
      setOptions(
        folders.map((folder) => ({
          id: folder.id,
          name: folder.name || "",
        }))
      );
    } else {
      setOptions([]);
    }
    setLoading(false);
  }, [integrationID, dispatch]);

  useEffect(() => {
    onFetchFolders();
  }, [onFetchFolders]);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <Autocomplete
          {...field}
          id={name}
          hasLabel={Boolean(label)}
          value={field.value}
          fullWidth={fullWidth}
          disabled={disabled || !integrationID}
          disableClearable={false}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          onChange={(_, value) => {
            field.onChange(value);
            onChangeAction(value);
          }}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          getOptionLabel={(option) => option?.name || ""}
          options={options}
          loading={loading}
          loadingText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress color="inherit" size={20} />
              <Typography variant="body2" component="span" lineHeight={1}>
                {t(LOADING)}
              </Typography>
            </Box>
          }
          renderOption={(props, option) => (
            <Box
              {...props}
              component="li"
              key={option.id}
              sx={{
                display: "flex",
                gap: 0.5,
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "background.paperContrastText" }}
              >
                {option.name}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              hiddenLabel={!label}
              label={label}
              variant={variant}
              placeholder={field?.value ? "" : placeholder}
              error={!!error}
              helperText={
                <Box
                  component="span"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>{error ? error?.message : helperText}</span>
                </Box>
              }
              sx={{
                backgroundColor: "fields.background",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                ...sxTextField,
                ".MuiFormHelperText-root": {
                  ml: "0 !important",
                },
              }}
            />
          )}
        />
      )}
    />
  );
};

export default SelectCanvaFolder;
